import {CatalogConfig, CatalogItem} from '../types'

// Pure helpers for the client-side list. The pilot fetches the full (small) catalog once and
// filters/paginates in memory so the search + active/inactive filter are correct across the
// whole dataset (the backend exposes no `active` query param). Kept dependency-free so they
// are trivially unit-testable.

export type StatusFilter = 'all' | 'active' | 'inactive'

// Whether this catalog exposes a soft-delete (status column, active/inactive filter, deactivate).
export const hasSoftDelete = (config: CatalogConfig): boolean => Boolean(config.softDelete)

// Only meaningful when the config has a softDelete. Callers guard on hasSoftDelete; for a config
// without one, everything is treated as active (there is no inactive state to represent).
export const isItemActive = (item: CatalogItem, config: CatalogConfig): boolean =>
  config.softDelete ? item[config.softDelete.field] === config.softDelete.activeValue : true

// The fields a search query is matched against. A config with no field flagged `searchable`
// has no search surface — callers should hide the search box (see hasSearch).
export const searchableFields = (config: CatalogConfig) =>
  config.fields.filter((field) => field.searchable)

export const hasSearch = (config: CatalogConfig): boolean => searchableFields(config).length > 0

export interface FilterOptions {
  search: string
  status: StatusFilter
  page: number
  pageSize: number
}

export interface FilterResult {
  items: CatalogItem[]
  total: number
  totalPages: number
}

export const filterCatalog = (
  all: CatalogItem[],
  config: CatalogConfig,
  {search, status, page, pageSize}: FilterOptions
): FilterResult => {
  const query = search.trim().toLowerCase()
  const searchFields = searchableFields(config)
  // No searchable fields configured → search is a no-op rather than filtering everything out.
  const applySearch = query.length > 0 && searchFields.length > 0

  const filtered = all.filter((item) => {
    // Status filtering only applies when the catalog has a soft-delete field.
    if (config.softDelete) {
      const active = isItemActive(item, config)
      if (status === 'active' && !active) return false
      if (status === 'inactive' && active) return false
    }
    if (!applySearch) return true
    return searchFields.some((field) =>
      String(item[field.name] ?? '')
        .toLowerCase()
        .includes(query)
    )
  })

  const total = filtered.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(page, 1), totalPages)
  const start = (safePage - 1) * pageSize
  const items = filtered.slice(start, start + pageSize)

  return {items, total, totalPages}
}
